/**
 * App — router + providers. QueryClient with the documented staleTime / gcTime;
 * BrowserRouter for the SPA shell; <Routes> mounted with the AppShell at the
 * authenticated root. RouteErrorBoundary wraps the authenticated Outlet to
 * catch render errors with the editorial fallback.
 */
import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from './components/AppShell';
import { AuthGuard } from './AuthGuard';
import { HouseholdGuard } from './HouseholdGuard';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';

import RequestMagicLink from './routes/auth/request';
import Verify from './routes/auth/verify';
import CreateHouseholdRoute from './routes/onboard/create-household';
import WelcomeRoute from './routes/onboard/welcome';
import AcceptInviteRoute from './routes/invites/accept';
import HomeRoute from './routes/home';
import PlacesListRoute from './routes/places/list';
import PlacesNewRoute from './routes/places/new';
import PlaceDetailRoute from './routes/places/detail';
import PlaceEditRoute from './routes/places/edit';
import VisitEditRoute from './routes/visits/edit';
import OfflineRoute from './routes/offline';
import SettingsRoute from './routes/settings';
// Settings sub-routes are lazy-loaded — they're low-traffic and adding them
// to the initial chunk costs ~25 KB gzipped. React.lazy + Suspense gates them
// behind the chunk fetch; the route shimmer is the loading fallback.
const CategoriesSettingsRoute = React.lazy(
  () => import('./routes/settings/categories'),
);
const TagsSettingsRoute = React.lazy(() => import('./routes/settings/tags'));
const HouseholdSettingsRoute = React.lazy(
  () => import('./routes/settings/household'),
);
const AccountSettingsRoute = React.lazy(
  () => import('./routes/settings/account'),
);
import { EmptyState } from './components/ui/EmptyState';
import { Button } from './components/ui/Button';
import { Skeleton } from './components/ui/Skeleton';

function NotFoundRoute() {
  return (
    <EmptyState
      title="Nothing here."
      description="The page you were looking for doesn't exist in this household."
      cta={
        <Button variant="primary" asChild>
          <a href="/">Back to your journal</a>
        </Button>
      }
    />
  );
}

/** Shimmer skeleton used as Suspense fallback for lazy-loaded routes. */
function RouteSkeleton() {
  return (
    <div data-testid="route-suspense-skeleton">
      <Skeleton className="h-3 w-24 mb-3" />
      <Skeleton className="h-12 w-2/3 mb-8" />
      <Skeleton className="h-6 w-full mb-3" />
      <Skeleton className="h-6 w-3/4 mb-3" />
      <Skeleton className="h-6 w-1/2" />
    </div>
  );
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry on 401/403/404 — those are routed by guards.
          const status = (error as { status?: number } | null)?.status;
          if (status && status >= 400 && status < 500) return false;
          return failureCount < 1;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export interface AppProps {
  queryClient?: QueryClient;
}

export function App({ queryClient }: AppProps = {}) {
  const client = React.useMemo(() => queryClient ?? makeQueryClient(), [queryClient]);
  return (
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <Routes>
          {/* Public auth routes */}
          <Route path="/auth/request" element={<RequestMagicLink />} />
          <Route path="/auth/verify" element={<Verify />} />

          {/* Offline fallback — must render without auth or network. */}
          <Route path="/offline" element={<OfflineRoute />} />

          {/* Semi-public — auth-aware */}
          <Route path="/invites/accept" element={<AcceptInviteRoute />} />

          {/* Authenticated — guarded */}
          <Route element={<AuthGuard />}>
            <Route
              path="/onboard/create-household"
              element={<CreateHouseholdRoute />}
            />
            <Route element={<HouseholdGuard />}>
              <Route
                path="/onboarding/welcome"
                element={<WelcomeRoute />}
              />
              <Route element={<AppShell />}>
                <Route
                  index
                  element={
                    <RouteErrorBoundary>
                      <HomeRoute />
                    </RouteErrorBoundary>
                  }
                />
                {/* Alias for the PWA manifest start_url=/home. */}
                <Route
                  path="/home"
                  element={
                    <RouteErrorBoundary>
                      <HomeRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/places"
                  element={
                    <RouteErrorBoundary>
                      <PlacesListRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/places/new"
                  element={
                    <RouteErrorBoundary>
                      <PlacesNewRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/places/:placeId"
                  element={
                    <RouteErrorBoundary>
                      <PlaceDetailRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/places/:placeId/edit"
                  element={
                    <RouteErrorBoundary>
                      <PlaceEditRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/visits/:visitId/edit"
                  element={
                    <RouteErrorBoundary>
                      <VisitEditRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RouteErrorBoundary>
                      <SettingsRoute />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/settings/categories"
                  element={
                    <RouteErrorBoundary>
                      <React.Suspense fallback={<RouteSkeleton />}>
                        <CategoriesSettingsRoute />
                      </React.Suspense>
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/settings/tags"
                  element={
                    <RouteErrorBoundary>
                      <React.Suspense fallback={<RouteSkeleton />}>
                        <TagsSettingsRoute />
                      </React.Suspense>
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/settings/household"
                  element={
                    <RouteErrorBoundary>
                      <React.Suspense fallback={<RouteSkeleton />}>
                        <HouseholdSettingsRoute />
                      </React.Suspense>
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/settings/account"
                  element={
                    <RouteErrorBoundary>
                      <React.Suspense fallback={<RouteSkeleton />}>
                        <AccountSettingsRoute />
                      </React.Suspense>
                    </RouteErrorBoundary>
                  }
                />
                <Route path="*" element={<NotFoundRoute />} />
              </Route>
            </Route>
          </Route>

          {/* Catch-all (unauthenticated) */}
          <Route path="*" element={<Navigate to="/auth/request" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

